import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { useWorkspace } from './WorkspaceContext';
import { useRealtime, RealtimeStatus } from './RealtimeContext';
import { invalidateLeagueSeason, leagueSyncEvent } from '@/domains/league/leagueRealtime';
import { leagueQueryKeys } from '@/domains/league/queryKeys';
import { setLeagueV2ActiveWorkspace } from '@/domains/league/api';

interface LeagueRuntimeValue {
  workspaceId: string;
  realtimeStatus: RealtimeStatus;
  registerDraft(key: string, active: boolean): void;
}

const LeagueRuntimeContext = createContext<LeagueRuntimeValue | null>(null);

export const LeagueRuntimeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const client = useQueryClient();
  const location = useLocation();
  const requested = useWorkspace().currentWorkspace?.id ?? '';
  const activeRoute = location.pathname === '/league' || location.pathname.startsWith('/league/');
  const previous = useRef(requested);
  const [ready, setReady] = useState(requested);
  const [drafts, setDrafts] = useState<Record<string, true>>({});
  const draftActive = useRef(false);
  const queued = useRef(new Set<number>());
  const realtime = useRealtime();
  const workspaceId = activeRoute && ready === requested ? requested : '';
  const registerDraft = useCallback((key: string, active: boolean) => setDrafts(current => {
    if ((active && current[key]) || (!active && !current[key])) return current;
    const next = { ...current };
    if (active) next[key] = true; else delete next[key];
    draftActive.current = Object.keys(next).length > 0;
    return next;
  }), []);

  useLayoutEffect(() => {
    let mounted = true;
    const switchWorkspace = async () => {
      const old = previous.current;
      setReady('');
      setLeagueV2ActiveWorkspace('');
      queued.current.clear();
      setDrafts({});
      draftActive.current = false;
      if (old && old !== requested) {
        await client.cancelQueries({ queryKey: leagueQueryKeys.root(old) });
        client.removeQueries({ queryKey: leagueQueryKeys.root(old) });
      }
      previous.current = requested;
      if (mounted) {
        setLeagueV2ActiveWorkspace(requested);
        setReady(requested);
      }
    };
    void switchWorkspace();
    return () => { mounted = false; };
  }, [client, requested]);

  useEffect(() => workspaceId ? realtime.subscribe(event => {
    const season = leagueSyncEvent(workspaceId, event);
    if (!season) return;
    if (draftActive.current) queued.current.add(season);
    else void invalidateLeagueSeason(client, workspaceId, season);
  }) : undefined, [client, realtime, workspaceId]);

  useEffect(() => {
    if (draftActive.current || !workspaceId || queued.current.size === 0) return;
    const seasons = Array.from(queued.current);
    queued.current.clear();
    seasons.forEach(season => { void invalidateLeagueSeason(client, workspaceId, season); });
  }, [client, drafts, workspaceId]);

  useEffect(() => () => queued.current.clear(), []);
  return <LeagueRuntimeContext.Provider value={{ workspaceId, realtimeStatus: realtime.status, registerDraft }}>{children}</LeagueRuntimeContext.Provider>;
};

export const useLeagueRuntime = (): LeagueRuntimeValue => {
  const value = useContext(LeagueRuntimeContext);
  if (!value) throw new Error('useLeagueRuntime must be used within LeagueRuntimeProvider');
  return value;
};

export const useLeagueDraft = (key: string, active: boolean): void => {
  const { registerDraft } = useLeagueRuntime();
  useEffect(() => {
    registerDraft(key, active);
    return () => registerDraft(key, false);
  }, [active, key, registerDraft]);
};
